import { jsx } from "panel";
import range from "lodash/range";
import take from "lodash/take";
import { ItemsMenuEl } from "common/components/items-menu";
import { InputEl } from "common/components/input";
import { ChartTooltipEl } from "../chart-tooltip";
import { Directions, PositionParentType } from "../drop-menu/types";
import { ChartLegendTheme } from "./constants";
function rollUpExtraItemsIntoOther(items, maxSections) {
  if (maxSections && items.length > maxSections) {
    const truncatedLegendSections = take(items, maxSections - 1);
    const hiddenSections = items.slice(maxSections - 1);
    truncatedLegendSections.push({
      label: `Next ${hiddenSections.length}`,
      tooltipSections: hiddenSections
    });
    items = truncatedLegendSections;
  }
  return items;
}
function LegendLoader() {
  return /* @__PURE__ */ jsx("div", {
    sel: ".loader"
  }, range(9).map((_, idx) => /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-large",
    key: idx
  })));
}
function TooltipSections({ tooltipSections }) {
  return /* @__PURE__ */ jsx(ChartTooltipEl, {
    props: { positioning: { positionParent: PositionParentType.ParentElement } }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".legend-rollup-segments"
  }, tooltipSections.map(({ color, label, tooltipSections: tooltipSections2 }) => /* @__PURE__ */ jsx("div", {
    sel: ".segment-text",
    style: { "--color": color }
  }, label, (tooltipSections2 == null ? void 0 : tooltipSections2.chartSection) ? /* @__PURE__ */ jsx(ChartTooltipEl, {
    attrs: {
      sections: tooltipSections2 == null ? void 0 : tooltipSections2.chartSection,
      "title-color": color,
      direction: Directions.Right
    },
    props: {
      positioning: { positionParent: PositionParentType.ParentElement }
    }
  }) : null))));
}
function ReadOnlyLegend({ selectedSections }) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".legend-container.legend-readonly"
  }, selectedSections.map((section, idx) => /* @__PURE__ */ jsx(null, null, idx > 0 ? /* @__PURE__ */ jsx("div", {
    sel: ".section-divider"
  }) : null, section.items.map((item, idx2) => /* @__PURE__ */ jsx("div", {
    sel: ".legend-segment",
    key: idx2
  }, /* @__PURE__ */ jsx("div", {
    sel: ".segment-icon"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".readonly-item-color",
    style: { "background-color": item.color }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".segment-text"
  }, /* @__PURE__ */ jsx("span", null, item.label)))))));
}
function CardTableLegend({
  el,
  sections,
  maxItems,
  maxItemWidth
}) {
  let { items } = sections[0];
  items = rollUpExtraItemsIntoOther(items, maxItems);
  return /* @__PURE__ */ jsx("div", {
    sel: ".legend-container.legend-table"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".legend-line"
  }, items.map((item, itemIdx) => {
    var _a;
    return /* @__PURE__ */ jsx("div", {
      sel: ".legend-segment",
      key: itemIdx,
      style: { "max-width": maxItemWidth != null ? maxItemWidth : `` },
      on: {
        mouseenter: item.tooltipSections ? () => el.update({ showChartTooltip: true }) : null,
        mouseleave: item.tooltipSections ? () => el.update({ showChartTooltip: false }) : null
      }
    }, item.label ? /* @__PURE__ */ jsx("div", {
      sel: ".segment-text",
      style: { "--color": item.color }
    }, item.label) : null, item.tooltipSections && el.state.showChartTooltip ? ((_a = item.tooltipSections) == null ? void 0 : _a.chartSection) ? /* @__PURE__ */ jsx(ChartTooltipEl, {
      attrs: {
        sections: item.tooltipSections.chartSection,
        "title-color": item.tooltipSections.color,
        direction: Directions.Down
      },
      props: {
        positioning: { positionParent: PositionParentType.ParentElement }
      }
    }) : /* @__PURE__ */ jsx(TooltipSections, {
      tooltipSections: item.tooltipSections
    }) : null);
  })));
}
function CardListLegend({ sections, maxItems }) {
  let { items } = sections[0];
  items = rollUpExtraItemsIntoOther(items, maxItems);
  return /* @__PURE__ */ jsx("div", {
    sel: ".legend-container.legend-list"
  }, items.map((item, idx) => {
    const text = item.label;
    return /* @__PURE__ */ jsx("div", {
      sel: ".legend-segment",
      key: idx
    }, /* @__PURE__ */ jsx("div", {
      sel: ".segment-text",
      style: { "--color": item.color },
      attrs: { title: text }
    }, text), item.tooltipSections && /* @__PURE__ */ jsx(TooltipSections, {
      tooltipSections: item.tooltipSections
    }));
  }));
}
function PrimaryLegend({
  sections,
  searchQuery,
  searchInput,
  showSelectAll,
  selectAllDisabled,
  selectAllTooltip,
  height,
  onClickItem,
  onSelectAll
}) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".chart-legend-container"
  }, /* @__PURE__ */ jsx(ItemsMenuEl, {
    style: { "max-height": height },
    attrs: {
      elref: `itemsMenu`,
      multiple: true,
      "search-filter": searchQuery,
      "show-select-all": showSelectAll,
      "select-all-disabled": selectAllDisabled,
      "select-all-tooltip": selectAllTooltip
    },
    props: {
      sections
    },
    on: {
      clickItem: onClickItem,
      selectAll: onSelectAll
    },
    hook: {
      insert: ({ elm }) => elm.keydownProxyEl = searchInput
    }
  }));
}
export function template() {
  var _a;
  const { searchQuery } = this.state;
  const sections = (_a = this.sections) != null ? _a : this.attr(`sections`) || this.helpers.getSections();
  const loading = this.attr(`loading`);
  const theme = this.attr(`theme`);
  let themedLegend;
  switch (theme) {
    case ChartLegendTheme.ReadOnly:
      themedLegend = /* @__PURE__ */ jsx(ReadOnlyLegend, {
        selectedSections: this.helpers.filterSelected(sections)
      });
      break;
    case ChartLegendTheme.CardList:
      themedLegend = /* @__PURE__ */ jsx(CardListLegend, {
        sections,
        maxItems: this.attr(`max-legend-items`)
      });
      break;
    case ChartLegendTheme.CardTable:
      themedLegend = /* @__PURE__ */ jsx(CardTableLegend, {
        el: this,
        sections,
        maxItems: this.attr(`max-legend-items`),
        maxItemWidth: this.attr(`max-table-item-width`)
      });
      break;
    default:
      themedLegend = /* @__PURE__ */ jsx(PrimaryLegend, {
        sections,
        searchQuery,
        searchInput: this.elRefs.searchInput,
        showSelectAll: this.attr(`show-select-all`),
        selectAllDisabled: this.attr(`select-all-disabled`),
        selectAllTooltip: this.attr(`select-all-tooltip`),
        height: this.helpers.getItemsMenuHeight(),
        onClickItem: this.helpers.toggledCheckbox,
        onSelectAll: this.helpers.toggledAllCheckboxes
      });
  }
  return /* @__PURE__ */ jsx("div", {
    sel: ".chart-legend",
    class: { [`chart-theme-${theme}`]: true },
    style: this.helpers.overrideStyle(),
    attrs: { elref: `wrapper` }
  }, theme === ChartLegendTheme.Primary && this.attr(`enable-search`) ? /* @__PURE__ */ jsx("div", {
    sel: ".chart-legend-search"
  }, /* @__PURE__ */ jsx(InputEl, {
    attrs: {
      icon: `search`,
      placeholder: `Search`,
      elref: `search`
    },
    hook: {
      insert: ({ elm }) => this.setElRef(`searchInput`, elm)
    },
    on: {
      input: this.helpers.updateLegendSearch
    }
  })) : null, loading ? /* @__PURE__ */ jsx(LegendLoader, null) : themedLegend);
}
